// shared.ts — Types, constants, and utility functions shared across Studio UI components

import type { CSSProperties } from "react";

// ---------------------------------------------------------------------------
// Identity helpers
// ---------------------------------------------------------------------------

export function getOrCreate(key: string, factory: () => string): string {
  const s = localStorage.getItem(key);
  if (s) return s;
  const v = factory();
  localStorage.setItem(key, v);
  return v;
}

export function colorForId(id: string): string {
  const palette = ["#f87171","#fb923c","#fbbf24","#34d399","#22d3ee","#60a5fa","#a78bfa","#f472b6"];
  let h = 0;
  for (const c of id) h = (h * 31 + c.charCodeAt(0)) & 0x7fffffff;
  return palette[h % palette.length];
}

export function deriveDisplayName(email: string, nickname: string): string {
  if (nickname.trim()) return nickname.trim();
  const emailPrefix = email.split("@")[0];
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-/.test(emailPrefix)) return "User";
  return emailPrefix || "User";
}

// ---------------------------------------------------------------------------
// Team colors
// ---------------------------------------------------------------------------

export const COLOR_A = "#FF6B6B";
export const COLOR_B = "#4ECDC4";
export const COLOR_C = "#FFE66D";
export const TEAM_COLORS = [COLOR_A, COLOR_B, COLOR_C] as const;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

// Mirrors shared/types/doc_status.ts — keep values in sync.
export type DocStatus = 'draft' | 'sample' | 'approved' | 'production' | 'rejected';
export type Lang = "en" | "fr" | "it" | "zh";
export type LandingTab = "mine" | "shared";

export const STATUS_CYCLE: DocStatus[] = ['draft', 'sample', 'approved', 'production'];
export const STATUS_NEXT: Record<DocStatus, DocStatus> = {
  draft:'sample', sample:'approved', approved:'production', production:'draft', rejected:'draft',
};
export const STATUS_STYLE: Record<DocStatus, { border: string; bg: string; color: string }> = {
  draft:      { border:"#3a3a3a", bg:"transparent",            color:"#6e6e6e" },
  sample:     { border:"#2a4a8a", bg:"rgba(42,90,160,0.15)",  color:"#7aabff" },
  approved:   { border:"#1a6a3a", bg:"rgba(26,106,58,0.15)",  color:"#5ae09a" },
  production: { border:"#7a4a10", bg:"rgba(180,100,20,0.15)", color:"#ffaa55" },
  rejected:   { border:"#6a1a1a", bg:"rgba(100,20,20,0.15)",  color:"#ff7070" },
};

export const LANGS: Lang[] = ["en","fr","it","zh"];
export const LANG_LABELS: Record<Lang,string> = { en:"EN", fr:"FR", it:"IT", zh:"中" };

const UI: Record<string, Partial<Record<Lang, string>>> = {
  back:            { en:"← Back",           fr:"← Retour",           it:"← Indietro",         zh:"← 返回"      },
  save:            { en:"Save",              fr:"Sauvegarder",        it:"Salva",               zh:"保存"         },
  saving:          { en:"Saving…",           fr:"Sauvegarde…",        it:"Salvataggio…",        zh:"保存中…"      },
  saved:           { en:"Saved",             fr:"Sauvegardé",         it:"Salvato",             zh:"已保存"       },
  save_failed:     { en:"Save failed",       fr:"Échec sauvegarde",   it:"Salvataggio fallito", zh:"保存失败"      },
  export_pdf:      { en:"Export PDF",        fr:"Exporter PDF",       it:"Esporta PDF",         zh:"导出 PDF"     },
  exporting:       { en:"Generating…",       fr:"Génération…",        it:"Generazione…",        zh:"生成中…"      },
  submit:          { en:"Submit",             fr:"Soumettre",          it:"Invia",               zh:"提交"         },
  my_styles:       { en:"My Styles",         fr:"Mes styles",         it:"I miei stili",        zh:"我的款号"      },
  shared:          { en:"Shared",             fr:"Partagé",            it:"Condiviso",           zh:"共享款号"      },
  new_pack:        { en:"+ New",              fr:"+ Nouveau",          it:"+ Nuovo",             zh:"+ 新建"       },
  empty_mine:      { en:"No styles — click + New to start", fr:"Aucun style — cliquez + Nouveau", it:"Nessuno stile — clicca + Nuovo", zh:"暂无款号 — 点击 + 新建" },
  empty_shared:    { en:"Shared styles coming soon",        fr:"Styles partagés bientôt",         it:"Stili condivisi presto",          zh:"共享款号功能即将推出"   },
  import_specflow:      { en:"Import from SpecFlow", fr:"Importer depuis SpecFlow", it:"Importa da SpecFlow", zh:"从 SpecFlow 导入" },
  import_from_file:     { en:"From .json file", fr:"Depuis un fichier .json", it:"Da file .json", zh:"从 .json 文件" },
  import_already:       { en:"Already imported — updates the existing pack", fr:"Déjà importé — met à jour le pack existant", it:"Già importato — aggiorna il pack esistente", zh:"已导入 — 将更新现有款号" },
  import_unknown_pack:  { en:"Unknown SpecFlow pack", fr:"Pack SpecFlow inconnu", it:"Pack SpecFlow sconosciuto", zh:"未知的 SpecFlow 款号" },
  import_invalid_file:  { en:"Invalid file", fr:"Fichier invalide", it:"File non valido", zh:"文件无效" },
  chat:            { en:"Chat",              fr:"Chat",               it:"Chat",                zh:"对话"         },
  activity:        { en:"Activity",          fr:"Activité",           it:"Attività",            zh:"动态"         },
  no_messages:     { en:"NO MESSAGES YET",   fr:"AUCUN MESSAGE",      it:"NESSUN MESSAGGIO",    zh:"暂无消息"      },
  no_activity:     { en:"NO ACTIVITY YET",   fr:"AUCUNE ACTIVITÉ",    it:"NESSUNA ATTIVITÀ",    zh:"暂无动态"      },
  msg_placeholder: { en:"Message…",          fr:"Message…",           it:"Messaggio…",          zh:"消息…"        },
  ai_thinking:     { en:"AI is thinking…",   fr:"L'IA réfléchit…",   it:"L'IA sta pensando…", zh:"AI 思考中…"    },
  // UX-COPY-1 — permission-denial messages (① what · ② why · ③ path; neutral,
  // non-blaming). EN + ZH finalized by founder; FR/IT fall back to EN via ui()
  // until founder finalizes them through this same layer. No dead action
  // buttons (request-access flow is v2.0) — plain "contact the owner" text.
  denial_delete_owner_only: { en:"You can edit this, but only the owner can delete it.", zh:"你可以编辑它，但只有所有者才能删除。" },
  denial_share_owner_only:  { en:"The owner shares and manages this folder. You can open and edit its styles, but sharing settings are owner-only. Contact the owner to change who has access.", zh:"此文件夹由所有者共享和管理。你可以打开并编辑其中的款号，但共享设置仅限所有者。如需更改访问权限请联系所有者。" },
  denial_access_revoked:    { en:"You're no longer part of this shared group. Please contact the owner to rejoin.", zh:"你已不在此共享团队中。请联系所有者以重新加入。" },
  denial_pack_no_access:    { en:"This folder is no longer shared with you. Please contact the owner.", zh:"此文件夹已不再与你共享。请联系所有者。" },
  denial_pack_removed:      { en:"This pack has been removed by the owner.", zh:"此款号已被所有者移除。" },
  // Losing access to ONE pack (the team leader removed it from the shared folder)
  // is NOT being removed from the team — the copy stays pack-scoped and neutral on
  // membership (founder wording, 2026-06-13).
  denial_pack_moved_out:    { en:"This techpack has been removed by the team leader and is no longer accessible. For more information, contact the team leader.", zh:"此款号已被团队负责人移除，将无法再访问。如需了解更多，请联系团队负责人。" },
  denial_folder_unshared:   { en:"This folder was returned to private and is no longer shared. Contact the owner if you need access.", zh:"此文件夹已设为私有，不再共享。如需访问请联系所有者。" },
  // Q2: a folder owner ejecting a member's contributed pack — returns it to the
  // creator's private area; it is NOT a delete and not a move into the owner's space.
  pack_eject_from_folder:   { en:"Remove from shared folder", fr:"Retirer du dossier partagé", it:"Rimuovi dalla cartella condivisa", zh:"移出共享文件夹" },
  invite_used:              { en:"This invite has already been used. Ask the owner for a new link.", zh:"此邀请链接已被使用。请向所有者索取新链接。" },
  invite_expired:           { en:"This invite has expired. Ask the owner to re-invite you.", zh:"此邀请已过期。请所有者重新邀请你。" },
  // SECURITY GATE (founder, 2026-06-13): the ONLY message + exit for a mismatched
  // invite. No mention of a workspace — the link is a gate, not a login entry.
  invite_not_for_account:   { en:"This invitation is not for your account. Please go back to login.", zh:"此邀请并非发给你的账户。请返回登录页面。" },
  back_to_login:            { en:"Back to login", fr:"Retour à la connexion", it:"Torna al login", zh:"返回登录" },
  // P2a-2a — audit-log event copy STUBS (EN+ZH) so studio_audit_events.message_key
  // never dangles. These render in the Activity panel; final wording + the
  // owner/subject-aware phrasing land in P2a-3 (read + UI).
  member_removed:           { en:"A member was removed from the shared folder.", zh:"一名成员已被移出共享文件夹。" },
  invite_created:           { en:"An invitation was sent.", zh:"已发送一封邀请。" },
  invite_cancelled:         { en:"A pending invitation was cancelled.", zh:"一封待接受的邀请已被取消。" },
};

export function ui(key: string, lang: string): string {
  const e = UI[key];
  return e?.[lang as Lang] ?? e?.en ?? key;
}

export interface TechPackEntry {
  id: string;
  styleNumber: string;
  productCategory: string;
  season: string;
  status: DocStatus;
  updatedAt: string;
  folderId?: string | null;
  isOwner?: boolean;   // P1a: server-provided; folder authority — gates pack controls (delete/move)
  isPackCreator?: boolean; // Q2: server-provided TRUE creatorship; folder owner gets eject-only on a member's pack
  memberRole?: "collaborator" | "viewer" | null; // server-provided folder_members.role for the member branch; null/undefined in own domain
}

export interface TeamMember {
  id: string;
  email: string;
  role: "OWNER" | "EDITOR" | "VIEWER";
  status: "active" | "pending";
}

// ---------------------------------------------------------------------------
// Storage
// ---------------------------------------------------------------------------

export const STORAGE_KEY = "studio_techpacks";
export const LANG_KEY    = "studio_language";

function _resolveWsBase(): string {
  const env = import.meta.env.VITE_WS_URL as string | undefined;
  if (env) return env;
  if (typeof window !== "undefined") {
    return `${window.location.protocol === "https:" ? "wss" : "ws"}://${window.location.host}`;
  }
  if (import.meta.env.PROD) throw new Error("VITE_WS_URL is required in production — check .env");
  return "ws://localhost:4000";
}
export const WS_BASE = _resolveWsBase();

export function loadPacks(): TechPackEntry[] {
  try {
    const p = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "[]");
    return Array.isArray(p) ? p : [];
  } catch { return []; }
}

export function savePacks(p: TechPackEntry[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
}

// ---------------------------------------------------------------------------
// Shared CSS helpers
// ---------------------------------------------------------------------------

export const F: CSSProperties = { fontFamily: "var(--font)" };

export const BTN: CSSProperties = {
  ...F, background: "transparent", border: "1px solid #3a3a3a", color: "#6e6e6e",
  fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", padding: "6px 12px",
  cursor: "pointer", textTransform: "uppercase" as const, whiteSpace: "nowrap" as const,
};

export const LBL: CSSProperties = {
  fontFamily: "'Rama Gothic E', system-ui", fontSize: "0.6rem",
  letterSpacing: "0.15em", textTransform: "uppercase" as const,
  color: "var(--text-muted)", marginBottom: 6, display: "block", fontWeight: "normal",
};
